import React, { useState, useCallback } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Alert
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { login } from '../services/authService'
import AppLogo from './AppLogo'
import type { AuthData } from '../services/authService'

interface LoginPageProps {
  onLoginSuccess: (authData: AuthData) => void
}

export default function LoginPage({ onLoginSuccess }: LoginPageProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!email.trim()) {
      setError('Informe seu e-mail ou nome de usuário')
      return
    }
    if (!password) {
      setError('Informe sua senha')
      return
    }

    setLoading(true)
    try {
      const authData = await login(email.trim(), password)
      onLoginSuccess(authData)
    } catch (err: any) {
      const msg = err?.message || 'Falha na autenticação'
      if (msg.includes('Invalid credentials') || msg.includes('UNAUTHORIZED')) {
        setError('E-mail/usuário ou senha inválidos')
      } else if (msg.includes('not configured') || msg.includes('Contact administrator')) {
        setError('Serviço de autenticação não configurado. Entre em contato com o administrador.')
      } else if (msg.includes('Unable to connect') || msg.includes('unavailable')) {
        setError('Não foi possível conectar ao serviço de autenticação. Tente novamente mais tarde.')
      } else {
        setError(msg)
      }
    } finally {
      setLoading(false)
    }
  }, [email, password, onLoginSuccess])

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #42a5f5 0%, #1976d2 50%, #ba68c8 100%)',
        p: 2
      }}
    >
      <Paper
        elevation={8}
        sx={{
          maxWidth: 420,
          width: '100%',
          p: 4,
          borderRadius: '12px',
          textAlign: 'center',
          bgcolor: 'background.paper'
        }}
      >
        {/* Logo / Header */}
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <AppLogo size="lg" shape="circle" hideTitle={true} />
          <Typography variant="body2" sx={{ pt: 4, pb: 2}} color="text.secondary">
            Faça login para acessar o sistema de transcrição
          </Typography>
        </Box>

        {/* Error message */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, textAlign: 'left' }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Login form */}
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label="E-mail ou Usuário"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon fontSize="small" color="action" />
                </InputAdornment>
              )
            }}
          />

          <TextField
            fullWidth
            label="Senha"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            sx={{ mb: 3 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(v => !v)}
                    edge="end"
                    size="small"
                    tabIndex={-1}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading}
            sx={{
              py: 1.5,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              }
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: '#fff' }} />
            ) : (
              'Entrar'
            )}
          </Button>
        </Box>

        {/* Footer */}
        <Typography variant="caption" color="text.disabled" sx={{ mt: 3, display: 'block' }}>
          Utilize suas credenciais da plataforma Criminal Player
        </Typography>
      </Paper>
    </Box>
  )
}
